'use client';

import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { Button } from '@/components/shared/ui/button';
import { Download, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useAuth } from '@/lib/integrations/supabase';
import { getPreferences } from '@/lib/features/data';
import { getBrowserClient } from '@/lib/integrations/supabase/client';

const AUTH_ROUTES = ['/', '/login', '/register', '/reset', '/reset-password'];

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [show, setShow] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(true);
  const { businessId } = useAuth();
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('show_install_prompt');
      if (stored !== null) {
        setShowInstallPrompt(stored === 'true');
      }
    }
  }, []);

  useEffect(() => {
    if (!businessId) return;

    const fetchPreference = async () => {
      try {
        const prefs = await getPreferences(businessId);
        if (prefs?.show_install_prompt !== undefined) {
          setShowInstallPrompt(prefs.show_install_prompt);
          if (typeof window !== 'undefined') {
            localStorage.setItem('show_install_prompt', String(prefs.show_install_prompt));
          }
        }
      } catch {
        // Keep default value on error
      }
    };

    fetchPreference();

    const supabase = getBrowserClient();
    const channel = supabase
      .channel(`preferences-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'preferences',
          filter: `business_id=eq.${businessId}`,
        },
        (payload) => {
          const updatedPrefs = payload.new as { show_install_prompt?: boolean };
          if (updatedPrefs.show_install_prompt !== undefined) {
            setShowInstallPrompt(updatedPrefs.show_install_prompt);
            if (typeof window !== 'undefined') {
              localStorage.setItem('show_install_prompt', String(updatedPrefs.show_install_prompt));
            }
            if (!updatedPrefs.show_install_prompt) {
              setShow(false);
              setDeferredPrompt(null);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [businessId]);

  useEffect(() => {
    if (!showInstallPrompt) {
      setShow(false);
      setDeferredPrompt(null);
      return;
    }

    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, [showInstallPrompt]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setShow(false);
    }
  };

  if (!show || AUTH_ROUTES.includes(pathname)) return null;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed bottom-24 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50"
        >
          <div className="bg-card border border-border shadow-xl rounded-xl p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <Download className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <div className="font-medium">Install App</div>
              <div className="text-sm text-muted-foreground">Add to home screen for better experience</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setShow(false)} className="h-8 w-8 p-0">
                <X className="w-4 h-4" />
              </Button>
              <Button size="sm" onClick={handleInstall}>
                Install
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
